import { useCallback } from "react"
import { trackEvent } from "@/lib/posthog"
import type { AccentTheme } from "@/lib/site-content"

// =============================================================================
// Event Types
// =============================================================================

export type AnalyticsEvent =
  // CTA
  | "cta_clicked"
  | "sticky_bar_closed"
  // Auth
  | "auth_google_click"
  | "auth_instagram_click"
  | "auth_continue_clicked"
  | "auth_different_email_clicked"
  | "auth_email_otp_click"
  | "auth_otp_verified"
  // Pricing
  | "pricing_plan_selected"
  | "pricing_toggle_changed"
  | "pricing_plans_cta_clicked"
  // Footer
  | "footer_link_clicked"
  | "social_media_clicked"
  | "accent_color_changed"
  // Giveaway
  | "giveaway_popup_dismissed"
  | "giveaway_popup_clicked"
  // Press Kit
  | "press_kit_download"

// =============================================================================
// Event Payloads
// =============================================================================

type BasePayload = {
  section?: string
}

type CtaClickedPayload = BasePayload & {
  cta_id: string
  button_text: string
}

type StickyBarClosedPayload = BasePayload & {
  location: string
}

type AuthGoogleClickPayload = BasePayload & {
  has_referral: boolean
}

type AuthInstagramClickPayload = BasePayload & {
  has_referral: boolean
}

type AuthEmailOtpClickPayload = BasePayload & {
  email: string
}

type AuthOtpVerifiedPayload = BasePayload & {
  email: string
  has_referral: boolean
}

type PricingPlanSelectedPayload = BasePayload & {
  plan_name: string
  plan_id?: string
  plan_type?: string
  billing_period: "yearly" | "monthly"
  price?: string | number
}

type PricingToggleChangedPayload = BasePayload & {
  billing_period: "yearly" | "monthly"
}

type PricingPlansCtaClickedPayload = BasePayload & {
  placement?: string
}

type FooterLinkClickedPayload = BasePayload & {
  link: string
  target?: string
  isExternal?: boolean
}

type SocialMediaClickedPayload = BasePayload & {
  platform: string
  url: string
}

type AccentColorChangedPayload = BasePayload & {
  accent_color: AccentTheme
  previous_accent_color: AccentTheme
  prompt: string
  prompt_variant: AccentTheme
  source: string
}

type GiveawayPopupClickedPayload = BasePayload & {
  location: string
}

type PressKitDownloadPayload = BasePayload & {
  asset_type: string
}

// =============================================================================
// Payload Map
// =============================================================================

export type EventPayloadMap = {
  cta_clicked: CtaClickedPayload
  sticky_bar_closed: StickyBarClosedPayload
  auth_google_click: AuthGoogleClickPayload
  auth_instagram_click: AuthInstagramClickPayload
  auth_continue_clicked: BasePayload
  auth_different_email_clicked: BasePayload
  auth_email_otp_click: AuthEmailOtpClickPayload
  auth_otp_verified: AuthOtpVerifiedPayload
  pricing_plan_selected: PricingPlanSelectedPayload
  pricing_toggle_changed: PricingToggleChangedPayload
  pricing_plans_cta_clicked: PricingPlansCtaClickedPayload
  footer_link_clicked: FooterLinkClickedPayload
  social_media_clicked: SocialMediaClickedPayload
  accent_color_changed: AccentColorChangedPayload
  giveaway_popup_dismissed: BasePayload
  giveaway_popup_clicked: GiveawayPopupClickedPayload
  press_kit_download: PressKitDownloadPayload
}

// =============================================================================
// React Hook
// =============================================================================

type HasRequiredKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? never : K
}[keyof T] extends never
  ? false
  : true

export function useAnalytics() {
  const track = useCallback(
    <E extends AnalyticsEvent>(
      ...args: HasRequiredKeys<EventPayloadMap[E]> extends true
        ? [event: E, payload: EventPayloadMap[E]]
        : [event: E, payload?: EventPayloadMap[E]]
    ) => {
      const [event, payload] = args
      trackEvent(event, {
        ...payload,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      })
    },
    []
  )

  return { track }
}
